import { IDiarySchedule, IDiaryScheduleRawTimes } from 'shared-types/index';
import ScheduleLookupService from 'external-services/schedule-lookup-service/scheduleLookup.service';

const NS = 'ScheduleFilterService';

interface IServices {
  id?: string;
  serviceKey?: string;
  name: string;
  startHour?: boolean;
  endHour?: boolean;
}

// exported access to static functions (use StatusServiceInstance for functions that rely on angular dependencies)
class ScheduleFilterService {
  private schedulesByDayNum: Map<number, IDiaryScheduleRawTimes[]>;

  getServicesFromGetSchedule = () => {
    return new Promise((resolve) => {
      const services: IServices[] = [];
      const servicesVal = ScheduleLookupService.getSchedules().then(
        (schedules: IDiaryScheduleRawTimes[]) => {
          const totalSchedules = schedules.length;
          for (let i = totalSchedules - 1; i >= 0; i--) {
            const schedule = schedules[i];
            const totalServices = schedule.services.length;
            for (let s = 0; s < totalServices; s++) {
              const service = schedule.services[s];
              // service
              const serviceKey = service.name.toLowerCase().replace(' ', '-');
              services.push({
                id: service._id,
                serviceKey: serviceKey,
                name: service.name,
                startHour: false,
                endHour: false,
              });
            }
          }
          services.unshift({
            name: 'All',
          });
          return this.removeDuplicates(services, 'id');
        }
      );
      resolve(servicesVal);
    });
  };

  removeDuplicates = (arr: any, type: string) => {
    return Object.values(
      arr.reduce((acc: any, cur: any) => Object.assign(acc, { [cur[type]]: cur }), {})
    );
  };

  clearSchedulesByDayNum() {
    console.log(NS, 'clearSchedulesByDayNum');
    this.schedulesByDayNum?.clear();
  }

  addToSchedulesByDayNum(schedule: IDiaryScheduleRawTimes): void {
    const dayOfWeek = this.getDayOfWeekFromScheduleId(schedule._id);
    if (!this.schedulesByDayNum) {
      this.schedulesByDayNum = new Map<number, IDiaryScheduleRawTimes[]>();
    }
    if (!this.schedulesByDayNum.get(dayOfWeek)) {
      this.schedulesByDayNum.set(dayOfWeek, []);
    }
    this.schedulesByDayNum.get(dayOfWeek).push(schedule);
  }

  /**
   * Pass in a day of the week (0 = Sunday, 6 = Saturday) and get back all the historical schedules created for that day.
   * The first call will cache the results in 'this.schedulesByDayNum' for easier future retrieval.
   * @param dayOfTheWeek: number 0-6
   * @param getSchedules: this is passed as a parameter to avoid circular dependency with scheduleService
   */
  getSchedulesByDayOfTheWeek(
    dayOfTheWeek: number,
    getSchedules: () => Promise<IDiaryScheduleRawTimes[]>
  ): Promise<IDiaryScheduleRawTimes[]> {
    return new Promise<IDiaryScheduleRawTimes[]>((resolve, reject) => {
      if (this.schedulesByDayNum?.get(dayOfTheWeek)) {
        // return the cached schedules
        resolve(this.schedulesByDayNum.get(dayOfTheWeek));
        return;
      }

      getSchedules()
        .then((schedules: IDiaryScheduleRawTimes[]) => {
          this.schedulesByDayNum = new Map<number, IDiaryScheduleRawTimes[]>();

          // using reverse so that most recent entries in the list override the earlier ones, when they have duplicate date suffixes
          schedules.reverse().forEach((schedule) => {
            const _dayOfWeek: number = this.getDayOfWeekFromScheduleId(schedule._id);
            if (schedule.dayNum !== _dayOfWeek) {
              // just checking the data is all legit
              console.warn(
                NS,
                `Dirty data detected: A schedule's 'dayNum' property does not match the id's suffix`,
                schedule
              );
            }
            if (_dayOfWeek > -1) {
              const schedulesForDayOfWeek = this.schedulesByDayNum.get(_dayOfWeek); // eg, all the historical Tuesday schedules
              if (!schedulesForDayOfWeek) {
                this.schedulesByDayNum.set(_dayOfWeek, []);
              }

              const dateStr = this.getScheduleDateString(schedule._id);
              if (
                dateStr &&
                schedulesForDayOfWeek?.some((s) => this.getScheduleDateString(s._id) === dateStr)
              ) {
                console.warn(
                  NS,
                  `Dirty data detected: There is more than 1 schedule with the date suffix "${dateStr}". Stopped schedule with same date suffix loading into the timeline.`,
                  schedule
                );
              } else {
                this.schedulesByDayNum.get(_dayOfWeek).push(schedule);
              }
            }
          });

          // console.log(NS, '----', this.schedulesByDayNum.get(dayOfTheWeek).map(s => s._id + ' ' + s.services.map(sv => sv.name).join(',')));

          resolve(this.schedulesByDayNum.get(dayOfTheWeek));
        })
        .catch((err: any) => {
          reject(err);
        });
    });
  }

  getDayOfWeekFromScheduleId(scheduleId: string): number {
    const idParts = scheduleId.split('_');
    const _dayOfWeekAsStr = String(idParts[2]);
    const _dayOfWeekAsNum: number = parseInt(_dayOfWeekAsStr);
    if (isNaN(_dayOfWeekAsNum) || _dayOfWeekAsNum >= 7) {
      console.warn(
        NS,
        `Tried to retrieve day for week "${_dayOfWeekAsStr}", but it is not a valid number. Must be 0-6.`,
        scheduleId
      );
      return -1;
    }
    return _dayOfWeekAsNum;
  }

  getScheduleDateString(scheduleId: string): string {
    const idParts = scheduleId.split('_');
    return idParts[idParts.length - 1];
  }
}

// creates a singleton instance to export (for functions that rely on angular dependencies)
const instance = new ScheduleFilterService();
export { instance as ScheduleFilterService };
